import { Controller } from "@hotwired/stimulus"
import { NotificationSubscription } from "../channels/notification_channel"

export default class extends Controller {
  static targets = ["bell", "badge", "dropdown", "list", "emptyState"]
  static values = {
    url: String
  }

  connect() {
    // Initialize ActionCable subscription
    this.initializeCableSubscription()

    // Fetch initial notifications
    this.fetchUnread()
  }

  disconnect() {
    // Unsubscribe from ActionCable when controller disconnects
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe()
    }
  }

  initializeCableSubscription() {
    // Create a new ActionCable subscription with callbacks
    this.notificationSubscription = new NotificationSubscription({
      connected: () => {
        console.log("Real-time notifications connected")
      },

      disconnected: () => {
        console.log("Real-time notifications disconnected")
      },

      rejected: () => {
        console.error("Real-time notifications rejected")
        // Fallback to periodic fetching if connection is rejected
        this.startFallbackPolling()
      },

      notificationCreated: (notification) => {
        console.log("New notification received:", notification)
        this.handleNewNotification(notification)
      },

      markedAsRead: (data) => {
        if (data.success) {
          this.fetchUnread()
        }
      },

      markedAllAsRead: (data) => {
        if (data.success) {
          this.fetchUnread()
        }
      },

      dismissed: (data) => {
        if (data.success) {
          this.fetchUnread()
        }
      }
    })

    // Subscribe to the channel
    this.notificationSubscription.subscribe()
  }

  handleNewNotification(notification) {
    // Update the badge count
    this.fetchUnread()

    // Check if it's an achievement and trigger celebration
    if (notification.notification_type === 'achievement_unlocked') {
      this.triggerAchievementCelebration(notification)
    }

    // Show browser notification if permitted
    this.showBrowserNotification(notification)

    // Optionally play a sound or show a visual indicator
    this.showNewNotificationIndicator()
  }

  triggerAchievementCelebration(notification) {
    // Dispatch custom event to trigger achievement celebration modal
    const event = new CustomEvent('achievement:unlocked', {
      detail: {
        id: notification.id,
        name: notification.metadata?.achievement_name || 'Achievement',
        description: notification.message,
        points: notification.metadata?.points_earned || 0,
        rarity: notification.metadata?.rarity || 'common',
        icon: this.getAchievementIcon(notification.metadata?.rarity)
      }
    })

    document.dispatchEvent(event)

    // Automatically mark this notification as read after showing celebration
    setTimeout(() => {
      this.markNotificationAsRead(notification.id)
    }, 2000)
  }

  showBrowserNotification(notification) {
    // Check if browser notifications are supported and permitted
    if ("Notification" in window && Notification.permission === "granted") {
      const options = {
        body: notification.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `notification-${notification.id}`,
        requireInteraction: notification.priority === 'urgent'
      }

      const browserNotification = new Notification(notification.title, options)

      // Close notification after 5 seconds
      setTimeout(() => browserNotification.close(), 5000)

      // Handle notification click
      browserNotification.onclick = () => {
        window.focus()
        if (notification.action_url) {
          window.location.href = notification.action_url
        }
        browserNotification.close()
      }
    }
  }

  showNewNotificationIndicator() {
    // Add a subtle animation to the bell icon
    if (this.hasBellTarget) {
      this.bellTarget.classList.add('animate-bounce')
      setTimeout(() => {
        this.bellTarget.classList.remove('animate-bounce')
      }, 1000)
    }
  }

  toggle(event) {
    event.preventDefault()
    this.dropdownTarget.classList.toggle('hidden')

    if (!this.dropdownTarget.classList.contains('hidden')) {
      this.fetchUnread()
    }
  }

  async fetchUnread() {
    try {
      const response = await fetch(this.urlValue)
      const data = await response.json()

      this.updateBadge(data.unread_count)
      this.updateList(data.notifications)

      // Check for achievement notifications and trigger celebration
      this.checkForAchievements(data.notifications)
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
    }
  }

  checkForAchievements(notifications) {
    // Find unread achievement notifications
    const achievementNotifications = notifications.filter(n =>
      n.type === 'achievement_unlocked' && !n.read
    )

    // Trigger celebration for the first unread achievement
    if (achievementNotifications.length > 0) {
      const notification = achievementNotifications[0]
      this.triggerAchievementCelebration(notification)
    }
  }

  getAchievementIcon(rarity) {
    const icons = {
      common: '🥉',
      uncommon: '🥈',
      rare: '🥇',
      epic: '💎',
      legendary: '👑'
    }
    return icons[rarity?.toLowerCase()] || '🏆'
  }

  async markNotificationAsRead(notificationId) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      await fetch(`/notifications/${notificationId}/mark_as_read`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        }
      })

      // Refresh notifications after marking as read
      this.fetchUnread()
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  updateBadge(count) {
    if (this.hasBadgeTarget) {
      if (count > 0) {
        this.badgeTarget.textContent = count > 99 ? '99+' : count
        this.badgeTarget.classList.remove('hidden')
        this.badgeTarget.classList.add('inline-flex')
      } else {
        this.badgeTarget.classList.add('hidden')
        this.badgeTarget.classList.remove('inline-flex')
      }
    }
  }

  updateList(notifications) {
    if (!this.hasListTarget) return

    if (notifications.length === 0) {
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove('hidden')
      }
      this.listTarget.innerHTML = ''
      return
    }

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add('hidden')
    }

    this.listTarget.innerHTML = notifications.map(notification =>
      this.notificationTemplate(notification)
    ).join('')
  }

  notificationTemplate(notification) {
    const priorityColors = {
      urgent: 'bg-red-50 border-l-4 border-red-500',
      high: 'bg-yellow-50 border-l-4 border-yellow-500',
      normal: 'bg-white',
      low: 'bg-gray-50'
    }

    const priorityClass = priorityColors[notification.priority] || priorityColors.normal
    const readClass = notification.read ? 'opacity-60' : 'font-medium'

    return `
      <div class="notification-item p-4 hover:bg-gray-50 cursor-pointer transition-colors ${priorityClass} ${readClass}"
           data-notification-id="${notification.id}"
           data-action="click->notifications#markAsRead">
        <div class="flex items-start justify-between">
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900">${this.escapeHtml(notification.title)}</p>
            ${notification.message ? `<p class="text-sm text-gray-600 mt-1">${this.escapeHtml(notification.message)}</p>` : ''}
            <p class="text-xs text-gray-500 mt-2">${notification.time_ago} ago</p>
          </div>
          ${!notification.read ? '<div class="ml-3 flex-shrink-0"><span class="inline-block w-2 h-2 bg-indigo-600 rounded-full"></span></div>' : ''}
        </div>
        ${notification.action_url ? `
          <a href="${notification.action_url}"
             class="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
            ${this.escapeHtml(notification.action_text || 'View')}
            <svg class="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        ` : ''}
      </div>
    `
  }

  async markAsRead(event) {
    const notificationItem = event.currentTarget
    const notificationId = notificationItem.dataset.notificationId

    if (!notificationId) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      await fetch(`/notifications/${notificationId}/mark_as_read`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json'
        }
      })

      notificationItem.classList.add('opacity-60')
      notificationItem.classList.remove('font-medium')

      // Refresh the notification list
      this.fetchUnread()
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  async markAllAsRead(event) {
    event.preventDefault()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const url = this.urlValue.replace('/unread', '/mark_all_as_read')

      await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })

      this.fetchUnread()
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    }
  }

  closeDropdown(event) {
    if (!this.dropdownTarget.contains(event.target) &&
        !this.bellTarget.contains(event.target)) {
      this.dropdownTarget.classList.add('hidden')
    }
  }

  // Fallback polling in case WebSocket connection fails
  startFallbackPolling() {
    if (this.pollingTimer) return

    console.log("Starting fallback polling (WebSocket unavailable)")
    this.pollingTimer = setInterval(() => {
      this.fetchUnread()
    }, 30000) // Poll every 30 seconds as fallback
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
